import { useState, useEffect, useCallback } from "react";
import { Diamond } from "lucide-react";

const CARDS = [
  {
    src: "https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=800&q=80",
    alt: "Premium leather bags",
    label: "Premium Leather Bags",
  },
  {
    src: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=800&q=80",
    alt: "Fine jewellery collection",
    label: "Fine Jewellery Collection",
  },
  {
    src: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=800&q=80",
    alt: "Athletic footwear",
    label: "Athletic Footwear",
  },
  {
    src: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=800&q=80",
    alt: "Apparel and textiles",
    label: "Apparel & Textiles",
  },
];

const SLOT_STYLES = [
  { translateZ: 0, rotateY: 0, scale: 1, opacity: 1, zIndex: 40 },
  { translateZ: -120, rotateY: -24, scale: 0.9, opacity: 0.55, zIndex: 30 },
  { translateZ: -200, rotateY: 0, scale: 0.85, opacity: 0.4, zIndex: 20 },
  { translateZ: -120, rotateY: 24, scale: 0.92, opacity: 0.5, zIndex: 10 },
];

const FLOAT_DELAYS = ["0s", "0.5s", "1s", "1.5s"];

function getRelativeIndex(cardIndex, currentIndex) {
  return (cardIndex - currentIndex + CARDS.length) % CARDS.length;
}

function DesktopShowcase() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [labelVisible, setLabelVisible] = useState(true);

  const goTo = useCallback((index) => {
    setLabelVisible(false);
    setTimeout(() => {
      setCurrentIndex(index);
      setLabelVisible(true);
    }, 150);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setLabelVisible(false);
      setTimeout(() => {
        setCurrentIndex((i) => (i + 1) % CARDS.length);
        setLabelVisible(true);
      }, 150);
    }, 4000);
    return () => clearInterval(id);
  }, [paused]);

  const frontLabel = CARDS[currentIndex].label;

  return (
    <div
      className="relative mx-auto w-full max-w-[380px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Live chip — top-right */}
      <div
        className="absolute top-0 right-0 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full glass-card text-xs font-medium text-primary shadow-sm"
        aria-live="polite"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        Live: 24 active export orders
      </div>

      {/* Glow + stage */}
      <div
        className="relative w-full h-[320px] sm:h-[340px] md:h-[360px] showcase-perspective"
        style={{ perspective: "1500px" }}
      >
        <div className="showcase-glow absolute inset-0 pointer-events-none" aria-hidden />

        {/* Ground shadow — inside stage */}
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 w-[200px] h-6 rounded-[50%] pointer-events-none z-0"
          style={{
            background: "radial-gradient(ellipse, rgba(20,33,61,0.16) 0%, transparent 70%)",
            filter: "blur(6px)",
          }}
          aria-hidden
        />

        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ transformStyle: "preserve-3d" }}
        >
          {CARDS.map((card, i) => {
            const slot = getRelativeIndex(i, currentIndex);
            const style = SLOT_STYLES[slot];
            const isFront = slot === 0;

            return (
              <div
                key={card.src}
                className="absolute w-[220px] sm:w-[240px] h-[280px] sm:h-[300px] transition-all duration-[600ms] ease-in-out"
                style={{
                  transform: `translateZ(${style.translateZ}px) rotateY(${style.rotateY}deg) scale(${style.scale})`,
                  opacity: style.opacity,
                  zIndex: style.zIndex,
                  transformStyle: "preserve-3d",
                }}
              >
                <div
                  className="showcase-card-float relative w-full h-full"
                  style={{ animationDelay: FLOAT_DELAYS[i] }}
                >
                <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-lg border border-accent/10 bg-surface">
                  <div className="absolute top-3 left-3 z-10 w-5 h-5 flex items-center justify-center">
                    <Diamond size={14} className="text-accent" strokeWidth={2.5} />
                  </div>
                  <img
                    src={card.src}
                    alt={card.alt}
                    loading={i === 0 ? "eager" : "lazy"}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                  {isFront && (
                    <div
                      className={`absolute bottom-4 left-4 right-4 z-10 transition-opacity duration-300 ${
                        labelVisible ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      <span className="inline-block px-3 py-1.5 rounded-full glass-card text-xs font-semibold text-primary">
                        {frontLabel}
                      </span>
                    </div>
                  )}
                </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-1">
        {CARDS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === currentIndex ? "w-8 bg-accent" : "w-2 bg-border hover:bg-muted"
            }`}
            aria-label={`Show product ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function MobileCarousel() {
  const [active, setActive] = useState(0);

  return (
    <div className="relative w-full">
      <div
        className="absolute top-2 right-2 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full glass-card text-xs font-medium text-primary shadow-sm"
        aria-live="polite"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        Live: 24 active export orders
      </div>

      <div
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-hide -mx-1 px-1"
        onScroll={(e) => {
          const el = e.currentTarget;
          const w = el.offsetWidth;
          const idx = Math.round(el.scrollLeft / w);
          setActive(Math.min(idx, CARDS.length - 1));
        }}
      >
        {CARDS.map((card) => (
          <article
            key={card.src}
            className="flex-shrink-0 w-full snap-center relative rounded-2xl overflow-hidden shadow-lg border border-accent/10 aspect-[4/5] max-h-[300px]"
          >
            <div className="absolute top-3 left-3 z-10">
              <Diamond size={14} className="text-accent" />
            </div>
            <img src={card.src} alt={card.alt} className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute bottom-4 left-4 right-4">
              <span className="inline-block px-3 py-1.5 rounded-full glass-card text-xs font-semibold text-primary">
                {card.label}
              </span>
            </div>
          </article>
        ))}
      </div>

      <div className="flex justify-center gap-2 mt-2">
        {CARDS.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${active === i ? "w-6 bg-accent" : "w-1.5 bg-border"}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function ProductShowcase3D() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (isMobile) return <MobileCarousel />;
  return <DesktopShowcase />;
}
